import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ReactElement } from 'react';
import { Link, type To } from 'react-router-dom';
import { cn } from '../util/classes.ts';

/**
 * Shared appearance of the button family: shape, interaction states, and the
 * `variant`/`intent` color pairs. Exported so components that are buttons with
 * a different footprint — `IconButton` — inherit the same look instead of
 * restating it.
 */
export const buttonVariants = cva(
    [
        'select-none inline-flex shrink-0 items-center justify-center gap-cozy whitespace-nowrap cursor-pointer',
        'h-interactive min-w-interactive rounded-default px-default',
        'state-disabled:pointer-events-none state-disabled:opacity-disabled',
        'transition duration-state ease-state',
        'active:translate-px',
    ],
    {
        variants: {
            variant: {
                primary: [
                    'bg-(--c-color-main) text-(--c-color-contrast)',
                    'hover:bg-[color-mix(in_oklch,var(--c-color-main)_var(--tint-hover),var(--tint-base))]',
                    // de-emphasize for nested containers
                    'l2:bg-(--c-color-main)/(--opacity-wash) l2:text-(--c-color-main)',
                    'l3:bg-(--c-color-second)/(--opacity-wash) l3:text-(--c-color-second)',
                    'l2:hover:bg-(--c-color-main)/(--opacity-wash-hover)',
                    'l3:hover:bg-(--c-color-second)/(--opacity-wash-hover)',
                ],
                secondary: [
                    'bg-(--c-color-second)/(--opacity-wash) text-(--c-color-second)',
                    'hover:bg-(--c-color-second)/(--opacity-wash-hover)',
                    // de-emphasize for nested containers
                    'l2l3:bg-transparent l2l3:text-(--c-color-second)',
                    'hover:l2l3:bg-(--c-color-second)/(--opacity-wash)',
                ],
            },
            intent: {
                default: [
                    // Primary actions sit one rung lighter than brand.
                    '[--c-color-main:var(--color-blue-500)]',
                    '[--c-color-contrast:var(--color-brand-contrast)]',
                    '[--c-color-second:var(--color-content)]',
                ],
                destructive: [
                    '[--c-color-main:var(--color-danger)]',
                    '[--c-color-contrast:var(--color-danger-contrast)]',
                    '[--c-color-second:var(--color-danger)]',
                ],
                ai: [
                    '[--c-color-main:var(--color-ai-2)]',
                    '[--c-color-contrast:var(--color-ai-contrast)]',
                    '[--c-color-second:var(--color-ai-2)]',
                ],
            },
        },
        compoundVariants: [
            {
                variant: 'primary',
                intent: 'ai',
                class: [
                    'not-l2l3:bg-linear-to-r not-l2l3:from-ai-1 not-l2l3:to-ai-2 not-l2l3:text-ai-contrast not-l2l3:shadow-lg not-l2l3:shadow-ai-alt-2/20',
                    'hover:not-l2l3:from-ai-alt-1 hover:not-l2l3:to-ai-alt-2 hover:not-l2l3:shadow-ai-alt-2/30',
                ],
            },
        ],
        defaultVariants: {
            variant: 'secondary',
            intent: 'default',
        },
    },
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export type ButtonIntent = NonNullable<ButtonVariantProps['intent']>;

/**
 * Property type for {@link Button}.
 */
export type ButtonProps = ButtonVariantProps & PolymorphicButtonBaseProps;

/**
 * An styled button component that automatically renders an anchor, a button, or
 * a react-router Link depending on what props are passed. For an unstyled
 * version, see {@link PolymorphicButtonBase}.
 */
export function Button({ className, intent, variant, ...props }: ButtonProps): ReactElement {
    return (
        <PolymorphicButtonBase className={cn(buttonVariants({ variant, intent }), className)} {...props} />
    );
}

/**
 * Property type for {@link PolymorphicButtonBase}.
 */
export type PolymorphicButtonBaseProps =
    | (ComponentPropsWithRef<'button'> & { href?: undefined; to?: undefined })
    | (ComponentPropsWithRef<'a'> & { href: string; to?: undefined })
    | (ComponentPropsWithRef<typeof Link> & { href?: undefined; to: To });

/**
 * An unstyled primitive that automatically renders an anchor, a button, or a
 * react-router Link depending on what props are passed. For a styled version,
 * see {@link Button}.
 */
export function PolymorphicButtonBase(props: PolymorphicButtonBaseProps): ReactElement {
    if (props.href !== undefined) {
        // eslint-disable-next-line jsx-a11y/anchor-has-content -- passthrough
        return <a {...props} />;
    }
    if (props.to !== undefined) {
        return <Link {...props} />;
    }
    return <ButtonPrimitive type="button" {...props} />;
}
